import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

/**
 * Breadcrumb navigation for schedule V2 flows.
 * Mirrors the styling cues from AnalyticsBreadcrumb but keeps
 * the API simple: pass additional crumbs beyond the Survey Admin root.
 */
const ScheduleBreadcrumb = ({ crumbs = [], includeRoot = true, className }) => {
  const navigate = useNavigate();
  const sentinelRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);

  const rootCrumb = {
    label: "Survey Admin",
    to: "/leader/audit/schedule-v2",
  };

  const trail = includeRoot ? [rootCrumb, ...crumbs] : [...crumbs];
  const lastIndex = trail.length - 1;

  const handleNavigate = (crumb) => {
    if (!crumb || (crumb.disabled && !crumb.onClick)) {
      return;
    }

    if (typeof crumb.onClick === "function") {
      crumb.onClick();
      return;
    }

    if (crumb.to) {
      navigate(crumb.to);
    }
  };

  const handleKeyDown = (event, crumb, isClickable) => {
    if (!isClickable) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleNavigate(crumb);
    }
  };

  useEffect(() => {
    if (!sentinelRef.current || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      {
        rootMargin: "0px 0px 0px 0px",
        threshold: 0,
      }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <StickySentinel ref={sentinelRef} aria-hidden="true" />
      <Wrapper
        className={className}
        role="navigation"
        aria-label="Schedule breadcrumb"
      >
        <BreadcrumbBackdrop isSticky={isSticky} />
        <Inner>
          {trail.map((crumb, index) => {
            const key = `${crumb?.label || "crumb"}-${index}`;
            const isActive = index === lastIndex || crumb?.active;
            const isClickable = Boolean(
              (crumb?.to || crumb?.onClick) &&
                index !== lastIndex &&
                !crumb?.disabled
            );

            return (
              <React.Fragment key={key}>
                <BreadcrumbItem
                  onClick={isClickable ? () => handleNavigate(crumb) : undefined}
                  onKeyDown={
                    isClickable
                      ? (event) => handleKeyDown(event, crumb, isClickable)
                      : undefined
                  }
                  clickable={isClickable}
                  active={isActive && !isClickable}
                  role={isClickable ? "button" : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  aria-disabled={crumb?.disabled ? "true" : undefined}
                  aria-current={isActive && !isClickable ? "page" : undefined}
                >
                  {crumb?.label}
                </BreadcrumbItem>
                {index < lastIndex && <Separator>/</Separator>}
              </React.Fragment>
            );
          })}
        </Inner>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.nav`
  position: sticky;
  top: 0;
  z-index: 280;
  margin: 0 0 24px;
  background: transparent;
`;

const BreadcrumbBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 100vw;
  height: 100%;
  transform: ${(props) =>
    props.isSticky ? "translate(-50%, 0%)" : "translate(-50%, -100%)"};
  opacity: ${(props) => (props.isSticky ? 1 : 0)};
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(248, 250, 255, 0.92) 100%
  );
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: ${(props) =>
    props.isSticky ? "0 20px 40px rgba(15, 23, 42, 0.14)" : "none"};
  transition: transform 0.24s ease, opacity 0.24s ease, box-shadow 0.24s ease,
    background 0.24s ease;
  pointer-events: none;
  z-index: 0;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 24px;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
`;

const BreadcrumbItem = styled.span`
  color: ${(props) => (props.active ? "#111827" : "#6B7280")};
  font-weight: ${(props) => (props.active ? "600" : "500")};
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: all 0.2s ease;

  &:hover {
    color: ${(props) =>
      props.clickable ? "#374151" : props.active ? "#111827" : "#6B7280"};
    text-decoration: ${(props) => (props.clickable ? "underline" : "none")};
    text-underline-offset: 3px;
  }
`;

const Separator = styled.span`
  user-select: none;
  color: #d1d5db;
`;

const StickySentinel = styled.div`
  position: relative;
  width: 100%;
  height: 1px;
  margin-top: -1px;
  opacity: 0;
  pointer-events: none;
`;

export default ScheduleBreadcrumb;
